import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAddUserIdentity } from '../../../domains/user/ui/hooks/useAddUserIdentity';
import type { PayLetterPaymentMethod } from '../../../domains/orders/services/orderServices';
import { getOneTimePaymentLaunchPath, getPgProvider } from '../../../utils/paymentProvider';
import type {
  addUserIdentityBody,
  addUserIdentityResult
} from '../../../domains/user/services/userIdentities';

const hasNestedError = (e: unknown): e is { error: { code: string; message: string } } => {
  return typeof e === 'object' && e !== null && 'error' in e;
};

export function useIdentityVerificationRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentQueryParams = new URLSearchParams(location.search);
  const redirectPath = currentQueryParams.get('redirect_path');
  const impUid = currentQueryParams.get('imp_uid');
  const merchantUid = currentQueryParams.get('merchant_uid');
  const initialSuccess = currentQueryParams.get('success') === 'true';

  const [isSuccess, setIsSuccess] = useState(initialSuccess);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const { amount, productName, productId, isBilling, type, autoPay } = (() => {
    const topAmount = currentQueryParams.get('amount');
    const topProductName = currentQueryParams.get('productName');
    const topProductId = currentQueryParams.get('productId');
    const topIsBilling = currentQueryParams.get('isBilling');
    const topType = currentQueryParams.get('type');
    const topAutoPay = currentQueryParams.get('autoPay');

    if (topAmount || topProductName || topProductId || topType || topAutoPay) {
      return {
        amount: topAmount,
        productName: topProductName,
        productId: topProductId,
        isBilling: topIsBilling === 'true',
        type: topType,
        autoPay: topAutoPay === 'true'
      };
    }

    if (redirectPath) {
      const decoded = decodeURIComponent(redirectPath);
      const url = new URL(decoded, window.location.origin);
      const sp = url.searchParams;

      return {
        amount: sp.get('amount'),
        productName: sp.get('productName'),
        productId: sp.get('productId'),
        isBilling: sp.get('isBilling') === 'true',
        type: sp.get('type'),
        autoPay: sp.get('autoPay') === 'true'
      };
    }

    return { amount: null, productName: null, productId: null, type: null, autoPay: false };
  })();
  
  const { mutateAsync: addUserIdentity } = useAddUserIdentity();

  useEffect(() => {
    if (isSuccess && impUid && merchantUid) {
      (async () => {
        try {
          const { data, error }: addUserIdentityResult = await addUserIdentity({
            imp_uid: impUid,
            merchant_uid: merchantUid
          } as addUserIdentityBody);
          if (error) {
            setIsSuccess(false);
            const nestedCode = hasNestedError(error) ? error.error.code : undefined;
            const nestedMessage = hasNestedError(error) ? error.error.message : undefined;

            if (nestedCode === '1002') {
              setIsDuplicate(true);
            }
            if (
              nestedCode === 'RecordNotUnique' &&
              nestedMessage === 'This key already identified'
            ) {
              setIsDuplicate(true);
            }
            return;
          }
          if (data?.userIdentity?.userIdentityId) {
            setIsSuccess(true);
          }
        } catch {
          setIsSuccess(false);
        }
      })();
    }
  }, [isSuccess, impUid, merchantUid, addUserIdentity]);

  const hasRequiredParams = Boolean(impUid && merchantUid);
  const isPayLetter = getPgProvider() === 'pay-letter';

  const handleClick = useCallback(async (paymentMethod?: PayLetterPaymentMethod) => {
    if (isSuccess) {
      if (type === 'invite') {
        navigate('/free-charge/invite', { replace: true });
        return;
      }

      if (isBilling) {
        if (isPayLetter) {
          const oneTimePaymentLaunchPath = getOneTimePaymentLaunchPath();
          navigate(
            `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${productName}&productId=${productId}&autoPay=true`
          );
          return;
        }
        navigate(
          `/payment/billing?amount=${amount}&productName=${productName}&productId=${productId}`
        );
        return;
      }
      const oneTimePaymentLaunchPath = getOneTimePaymentLaunchPath();
      navigate(
        `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${productName}&productId=${productId}${paymentMethod ? `&paymentMethod=${paymentMethod}` : ''}${autoPay ? '&autoPay=true' : ''}`
      );
    } else {
      navigate(redirectPath ? decodeURIComponent(redirectPath) : '/bettie-chat-list', {
        replace: true
      });
    }
  }, [
    isSuccess,
    navigate,
    redirectPath,
    amount,
    productName,
    productId,
    isBilling,
    type,
    autoPay,
    isPayLetter
  ]);

  const needsPayLetterMethodSelection =
    isSuccess && isPayLetter && type !== 'invite' && !isBilling && !autoPay;

  return {
    isSuccess,
    hasRequiredParams,
    handleClick,
    isDuplicate,
    needsPayLetterMethodSelection
  };
}
